import { useSyncExternalStore } from "react";

/**
 * Is the API down for maintenance (a redeploy)?
 *
 * The web is a static site that stays up while the API restarts, so during a
 * deploy every API call fails with a network error or a 502/503/504 from the
 * host. Rather than let each page show its own broken state, any such failure
 * (reported by the shared fetcher) starts a health probe; when the API keeps
 * failing it, the app shows one "maintenance" screen, and it clears itself as
 * soon as the API answers again — no reload, so unsaved editor work survives.
 *
 * A single failure is not enough (a flaky connection is not maintenance): the
 * screen appears only after `confirmFailures` consecutive failed probes, and
 * never while the browser reports it is offline. Inert until `watchApiStatus`
 * is called (the app entry does; unit tests of other code never do).
 */

export interface ApiStatusOptions {
  /** One health check: resolves true when the API answered OK. */
  checkHealth: () => Promise<boolean>;
  /** Pause between probes. */
  probeDelayMs: number;
  /** Consecutive failed probes before the maintenance screen shows. */
  confirmFailures: number;
  /** Whether the browser thinks it is online. */
  isOnline: () => boolean;
  /** Timer (injectable for tests). */
  setTimer: (fn: () => void, ms: number) => unknown;
}

const HEALTH_TIMEOUT_MS = 8000;

let options: ApiStatusOptions | null = null;
let down = false;
let probing = false;
let failures = 0;
const listeners = new Set<() => void>();
const backListeners = new Set<() => void>();

function setDown(next: boolean): void {
  if (down === next) return;
  down = next;
  for (const listener of listeners) listener();
  if (!next) for (const listener of backListeners) listener();
}

/** Health check against `healthUrl` with a timeout; any error counts as a failure. */
export function healthChecker(healthUrl: string): () => Promise<boolean> {
  return async () => {
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), HEALTH_TIMEOUT_MS);
      try {
        const response = await fetch(healthUrl, { cache: "no-store", signal: controller.signal });
        return response.ok;
      } finally {
        clearTimeout(timer);
      }
    } catch {
      return false;
    }
  };
}

/** Turn the watch on (the app entry). Later calls replace the options. */
export function watchApiStatus(next: Partial<ApiStatusOptions> & Pick<ApiStatusOptions, "checkHealth">): void {
  options = {
    probeDelayMs: 4000,
    confirmFailures: 2,
    isOnline: () => (typeof navigator === "undefined" ? true : navigator.onLine !== false),
    setTimer: (fn, ms) => setTimeout(fn, ms),
    ...next
  };
}

async function probe(): Promise<void> {
  const current = options;
  if (!current) {
    probing = false;
    return;
  }
  const ok = await current.checkHealth();
  if (ok) {
    probing = false;
    failures = 0;
    setDown(false);
    return;
  }
  // Offline is the reader's connection, not our maintenance: stop and wait for
  // the next failure report instead of blaming the site.
  if (!current.isOnline()) {
    probing = false;
    failures = 0;
    return;
  }
  failures += 1;
  if (failures >= current.confirmFailures) setDown(true);
  current.setTimer(() => void probe(), current.probeDelayMs);
}

/**
 * An API call failed the way a restarting API fails (network error, 502, 503,
 * 504). Starts probing unless a probe is already running.
 */
export function reportApiTrouble(): void {
  if (!options || probing || !options.isOnline()) return;
  probing = true;
  void probe();
}

/** A response status that means "the API is (re)starting". */
export function isRestartStatus(status: number): boolean {
  return status === 502 || status === 503 || status === 504;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Whether the maintenance screen should show. */
export function useApiDown(): boolean {
  return useSyncExternalStore(subscribe, () => down, () => false);
}

/** Run `fn` when the API comes back after being down (e.g. to refresh lists). */
export function onApiBack(fn: () => void): () => void {
  backListeners.add(fn);
  return () => backListeners.delete(fn);
}

/** Test helper: forget all state and switch the watch off. */
export function resetApiStatus(): void {
  options = null;
  down = false;
  probing = false;
  failures = 0;
  listeners.clear();
  backListeners.clear();
}
